'use client';

import ImageNotSupportedRoundedIcon from '@mui/icons-material/ImageNotSupportedRounded';
import PersonIcon from '@mui/icons-material/Person';
import { Stack, Theme, Typography } from '@mui/material';
import { ReactElement, useState } from 'react';

/** The design's fixed portrait slot. See the module docblock for why these are raw literals. */
const WIDTH = 116;
const HEIGHT = 154;

/**
 * A small image tile: the full capture shown smaller, beside the outcome it produced. Fixed at
 * 116 x 154, the design's own portrait slot, rather than a caller-supplied box, because a
 * thumbnail's whole job is to be a consistent tile regardless of which capture it is showing.
 *
 * **116 and 154 are raw px literals, not tokens.** `theme.semantic.size` jumps from `4xs`
 * (4rem/64px) straight to `3xs` (8rem/128px); nothing in the scale lands on either dimension.
 * Recorded as a follow-up to tokenise once the design system grows a slot sized for this.
 *
 * **Shrinks below 768px instead of overflowing.** `max-width: 100%` plus `aspect-ratio` let the
 * tile track a narrower ancestor proportionally; there is no hardcoded breakpoint. The frozen
 * prototype has no such rule at all and its own fixed box overflows its card at 375px, covering
 * the card beside it, which this must not repeat.
 *
 * As with `CaptureViewer`, there is no capture-image service to report intrinsic dimensions, so
 * the image sizes itself with CSS (`object-fit: contain`) instead of a caller-computed fit.
 */
export type TEvidenceThumbnailProps = {
  /**
   * The capture image. Shrunk to fit the tile; never enlarged, never cropped. Omit to render the
   * "No capture yet" empty state: a customer who never did the check is a fact this tile has to
   * be able to show on its own, not only a capture or its load failure.
   */
  src?: string;
  /** Accessible label for the image. */
  alt: string;
  /** Presents the capture on black, for video stills. */
  onDark?: boolean;
  /**
   * Called if the capture image fails to load, e.g. an expired or 404 URL. This package has no
   * logging utility and must not grow one: this callback is the only way such a failure reaches
   * the surrounding app. The visible failure state renders regardless of whether a callback is
   * supplied.
   */
  onError?: () => void;
  /**
   * The tile's own testid, for a sizing assertion. Optional: ADR 72's usual rule still holds when
   * a capture is showing, and each instance is reached by its own `alt` first, because thumbnails
   * appear in sets and one shared testid would need a distinct value per instance with nothing
   * checking it. This exists because neither the empty state nor the tile's own box has an `alt`
   * to hang a lookup off.
   */
  'data-testid'?: string;
};

export default function EvidenceThumbnail({
  src,
  alt,
  onDark,
  onError,
  'data-testid': dataTestId,
}: Readonly<TEvidenceThumbnailProps>): ReactElement {
  const [hasErrored, setHasErrored] = useState(false);

  // `height: 'auto'` rather than a fixed 154 is what makes `aspectRatio` do anything: CSS ignores
  // `aspect-ratio` once both dimensions are already definite. Left auto, the browser derives the
  // height from whatever width `maxWidth: '100%'` has left the tile with, so a narrower ancestor
  // shrinks the whole tile proportionally instead of the box overflowing it.
  const tileSize = (t: Theme) => ({
    width: WIDTH,
    height: 'auto' as const,
    maxWidth: '100%',
    aspectRatio: `${WIDTH} / ${HEIGHT}`,
    borderRadius: t.semantic.borderRadius.sm,
  });

  if (!src) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        data-testid={dataTestId}
        sx={(t: Theme) => ({
          ...tileSize(t),
          gap: t.semantic.spacing['2xs'],
          border: `${t.semantic.spacing['4xs']} dashed ${t.palette.divider}`,
        })}
      >
        <PersonIcon
          sx={{
            // A raw literal in the frozen prototype. No semantic.fontSize or icon-size token
            // matches 32px without borrowing an unrelated one: theme.semantic.fontSize['4xl'] is
            // also 32px, but that is h3's typography size, and coupling this icon to it would
            // resize the icon if h3's heading size ever changed for typographic reasons. Kept as
            // a literal; recorded as debt alongside the tile's own 116 x 154 literals above.
            fontSize: 32,
            color: 'action.disabled',
          }}
        />
        <Typography variant="caption" color="text.secondary" textAlign="center">
          No capture yet
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      data-testid={dataTestId}
      sx={(t: Theme) => ({
        ...tileSize(t),
        overflow: 'hidden',
        backgroundColor: onDark ? t.palette.common.black : t.palette.background.default,
      })}
    >
      {hasErrored ? (
        // Icon only, not the same "Capture failed to load" wording CaptureViewer uses: the tile
        // has no room for that sentence. `titleAccess` is MUI's own mechanism for a meaningful
        // (non-decorative) icon: it sets role="img" and an accessible name, keeping one in the
        // tile's place once the failed `<img>`'s own `alt` is gone. A bare `aria-label` would not
        // do this on its own: `SvgIcon` defaults to `aria-hidden`, which removes the element from
        // the accessibility tree regardless of any label supplied.
        <ImageNotSupportedRoundedIcon
          titleAccess={`${alt} failed to load`}
          fontSize="small"
          sx={(t: Theme) => ({
            color: onDark ? t.palette.common.white : t.palette.text.secondary,
          })}
        />
      ) : (
        <img
          src={src}
          alt={alt}
          onError={() => {
            setHasErrored(true);
            onError?.();
          }}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            display: 'block',
          }}
        />
      )}
    </Stack>
  );
}
